import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Page } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { logError } from '@zg-rentals/log-error';
import { Head } from '@zg-rentals/next/app';

import { useGetPropertiesList } from '../../hooks/queries/properties/zrm';
import { usePropertiesPageUserOnboarding } from '../../hooks/usePropertiesPageUserOnboarding';
import accessUtils from '../../utils/accessUtils';
import {
  defaultFlexiblePropertiesFilters,
  initializeFlexiblePropertiesPageStateFromQuery,
} from '../../utils/flexiblePropertiesPageUtils';
import {
  removeQueryParams,
  renderInitialDisplayModal,
  transformFiltersToApiParams,
} from '../../utils/propertiesPageUtils';

import {
  DEFAULT_FLEXIBLE_PROPERTIES_VIEW_MODE,
  type FlexiblePropertiesViewMode,
} from '../../constants/FlexiblePropertiesConstants';
import PropertiesConstants from '../../constants/PropertiesConstants';
import QueryConstants from '../../constants/QueryConstants';
import { EXTERNAL_ROUTES } from '../../constants/urls';

import { useAuth } from '../../components/AuthProvider';
import AddPropertyModal from '../../components/modals/AddPropertyModal';
import PaymentsAggregateViewModal from '../../components/modals/PaymentsAggregateViewModal';
import UserOnboarding from '../../components/modals/UserOnboarding';
import EmailVerification from '../../components/properties/EmailVerification';
import { FlexiblePropertyEmptyState } from '../../components/properties/FlexiblePropertyEmptyState';
import NotificationBanner from '../../components/properties/NotificationBanner';
import { PropertiesPageNotifications } from '../../components/properties/PropertiesPageNotifications';
import { Toolbar } from './components/Toolbar';

import AuthCheckWrapper from '../AuthCheckWrapper';
import { PageHeading } from '../PropertiesPage/PageHeading';
import { PropertiesPageSkeleton } from '../PropertiesPage/PropertiesPageSkeleton';

import type { FlexiblePropertiesFilterState } from '../../utils/flexiblePropertiesPageUtils';

/*
 * TODO: Parity gaps vs PropertiesPage:
 *
 * 1. Analytics - emitViewPropertiesPageEvent on mount, data-eventid on Page.Root,
 *    and add-property clickstream events.
 *
 * 2. Properties list - PropertyTable wiring to API.
 */
const FlexiblePropertiesPage = () => {
  const router = useRouter();
  const { user } = useAuth();

  const isCheckInitialDisplayModal = useRef(true);
  const hasInitializedFromQuery = useRef(false);

  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showPaymentsAggregateViewModal, setShowPaymentsAggregateViewModal] = useState(false);
  const [filters, setFilters] = useState<FlexiblePropertiesFilterState>(defaultFlexiblePropertiesFilters);
  const [viewMode, setViewMode] = useState<FlexiblePropertiesViewMode>(DEFAULT_FLEXIBLE_PROPERTIES_VIEW_MODE);

  useEffect(() => {
    if (!router.isReady || hasInitializedFromQuery.current) {
      return;
    }

    hasInitializedFromQuery.current = true;

    const { filters: initialFilters, viewMode: initialViewMode } = initializeFlexiblePropertiesPageStateFromQuery(
      router.query,
    );

    setFilters(initialFilters);
    setViewMode(initialViewMode);
  }, [router.isReady]);

  const skeleton = <PropertiesPageSkeleton />;

  const {
    data: propertiesData,
    error: propertiesError,
    isError,
    isPending,
    isFetching,
    // fetchNextPage,
    // hasNextPage,
    isFetchingNextPage,
  } = useGetPropertiesList(
    transformFiltersToApiParams({
      subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
      searchText: filters.searchText,
    }),
    {
      enabled: Boolean(user?.loggedIn && user?.restrictions?.isAllowed),
    },
  );

  const { listingCount } = propertiesData ?? { listingCount: undefined, pager: undefined };

  const { total: numTotalListings = 0, archived: numArchivedListings = 0 } = listingCount ?? {};

  const {
    canAccessUserOnboardingNewUser,
    setShowingUserOnboardingExisitngUser,
    canAccessUserOnboardingExistingUser,
    onboardingConfig,
  } = usePropertiesPageUserOnboarding({ numTotalListings });

  const isContentLoading = isPending || isFetching || isFetchingNextPage;
  const hasAnyListings = numTotalListings > 0 || numArchivedListings > 0;

  useEffect(() => {
    if (
      user?.features?.canAccessMainPayments &&
      !user?.hasSeenPaymentsAggregatedViewModal &&
      (user?.paymentsAcceptedCount ?? 0) >= 2
    ) {
      setShowPaymentsAggregateViewModal(true);
    }
  }, [user?.features?.canAccessMainPayments]);

  useEffect(() => {
    if (isCheckInitialDisplayModal.current && !isPending) {
      isCheckInitialDisplayModal.current = false;
      checkInitialDisplayModal();
    }
  }, [isPending, numTotalListings]);

  useEffect(() => {
    if (isError && propertiesError) {
      logError({
        error: propertiesError,
        context: 'fetchGroupableProperties',
      });
    }
  }, [isError, propertiesError]);

  useEffect(() => {
    const postingPath = router.query[QueryConstants.POSTING_PATH];

    if (postingPath) {
      checkInitialDisplayModal();
    }
  }, [router.query[QueryConstants.POSTING_PATH]]);

  const checkInitialDisplayModal = () => {
    renderInitialDisplayModal({
      router,
      totalListingsWithoutFilters: numTotalListings,
      showAddPropertyModal,
      setShowAddPropertyModal: handleAddPropertyClick,
      canAccessUserOnboardingNewUser,
      setShowingUserOnboardingExisitngUser,
      canAccessUserOnboardingExistingUser,
    });
  };

  const handleAddPropertyClick = () => {
    if (user?.features?.canAddListing && user?.restrictions?.isAllowed) {
      setShowAddPropertyModal(true);
    }

    // TODO: add flexible properties analytics
    // emitOpenEvent({ user: user as ZrmClickstreamUser });
    // emitAddPropertyClickEvent({ user: user as ZrmClickstreamUser });
  };

  const canAccessFeatureNotifications = user ? accessUtils.canAccessFeatureNotifications({ user }) : false;

  return (
    <AuthCheckWrapper loading={skeleton} redirecting={skeleton}>
      <Page.Root data-testid="FlexiblePropertiesPage" css={{ backgroundColor: 'bg.neutral' }}>
        <Head applicationName="zillow-rental-manager-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
          <title>Properties | Zillow Rental Manager</title>
        </Head>

        {canAccessFeatureNotifications && <NotificationBanner />}

        {/* Page Heading */}
        <PageHeading
          buttonEmphasis="filled"
          buttonIconPosition="start"
          buttonIcon={<IconPlusFilled />}
          buttonSize="sm"
          headingTextStyle="heading-xs"
          handleAddPropertyClick={handleAddPropertyClick}
        />

        <EmailVerification />
        <PropertiesPageNotifications />

        {/* Content / Tables */}
        <Page.Content css={{ padding: { base: '0 {spacing.600}', xl: '0 {spacing.1200}' } }}>
          {hasAnyListings && (
            <Toolbar
              searchText={filters.searchText}
              onSearchTextChange={(searchText) => setFilters((current) => ({ ...current, searchText }))}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              isLoading={isContentLoading}
              isSearching={isContentLoading && Boolean(filters.searchText)}
            />
          )}

          {!hasAnyListings && !isContentLoading && (
            <FlexiblePropertyEmptyState onAddPropertyClick={handleAddPropertyClick} />
          )}

          {/* TODO: add load more properties once we work on the properties list */}
          {/* {hasNextPage && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '600' }}>
              <Button tone="brand" emphasis="filled" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                Load more properties
              </Button>
            </Box>
          )} */}
        </Page.Content>

        {/* Modals */}
        {onboardingConfig && <UserOnboarding {...onboardingConfig} />}

        {showAddPropertyModal && (
          <AddPropertyModal
            isOpen={showAddPropertyModal}
            onClose={() => {
              setShowAddPropertyModal(false);
              removeQueryParams(router, [QueryConstants.POSTING_PATH, QueryConstants.REDIRECT_TO]);
            }}
          />
        )}
        {showPaymentsAggregateViewModal && (
          <PaymentsAggregateViewModal
            onClose={() => setShowPaymentsAggregateViewModal(false)}
            paymentsAggregatePageUrl={EXTERNAL_ROUTES.PAYMENTS_DASHBOARD}
          />
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};

export default FlexiblePropertiesPage;
